import React, { useMemo, useCallback, useRef, type ReactNode } from 'react'
import { useHistory, useQueryParams, useLocation, useRouteMatch, stringifyQuery } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import { MY_SCHEDULE_TAB, FULL_SCHEDULE_TAB } from '@fs/zion-rootstech'
import {
  ScheduleProvider as ZionScheduleProvider,
  useScheduleContext as useZionScheduleContext,
} from '@fs/zion-rootstech/dist/es/providers/ScheduleProvider'
import type { ScheduleContextValue as ZionScheduleContextValue } from '@fs/zion-rootstech'
import { useMyScheduleContext } from '../MyScheduleProvider'
import { useActivateCalendarData, useCalendarDataContext } from '../CalendarDataProvider'

interface ExtendedScheduleContextValue extends ZionScheduleContextValue {
  tab: string
}

const omitTabFromQuery = (queryObj: Record<string, string>): Record<string, string> => {
  const result = { ...queryObj }
  delete result.tab
  return result
}

export function useScheduleContext(): ExtendedScheduleContextValue {
  const zionContext = useZionScheduleContext()
  return {
    ...zionContext,
    tab: zionContext.isMySchedulePage ? MY_SCHEDULE_TAB : FULL_SCHEDULE_TAB,
  }
}

export interface ScheduleProviderProps {
  children: ReactNode
}

export default function ScheduleProvider({ children }: ScheduleProviderProps): JSX.Element {
  // Get calendar data from context (single source of truth)
  const { calendarData, loading: calendarLoading } = useCalendarDataContext()

  // This provider is the only consumer of calendar-derived state, so it is also what turns the fetch
  // on. Because it is mounted by the two schedule routes and nothing else, the calendar is fetched
  // exactly where it is read, without CalendarDataProvider knowing anything about routes or phases.
  useActivateCalendarData()

  const query = useQueryParams()
  const history = useHistory()
  const location = useLocation()
  const user = useUser()

  const userObj = useMemo(() => ({ signedIn: user?.signedIn ?? false }), [user?.signedIn])

  // Get mySchedule from MyScheduleProvider context
  const { mySchedule } = useMyScheduleContext()

  // This provider is mounted by both schedule routes, so it has to work out which one it is under.
  // Matched against the path User.route.tsx declares rather than substring-tested, so it agrees with
  // the route that actually rendered.
  const isMySchedulePage = Boolean(useRouteMatch('/user/schedule'))

  // Zion seeds its reducer from this once and never re-reads it, which is why the provider is mounted
  // per route (ScheduleRoot/MyScheduleRoot) instead of app-wide: a mount is a route entry, so reading
  // the query once per mount reads it once per entry. App-wide, these filters would be frozen at
  // whatever the URL held when the app first loaded.
  const initialFilters = useMemo(() => {
    const cleanQuery = omitTabFromQuery(query.query)
    return {
      day: cleanQuery.day,
      type: cleanQuery.type || 'allsessions',
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onFilterChangeRef = useRef<(filters: { day?: string; type?: string }) => void>()

  onFilterChangeRef.current = (filters: { day?: string; type?: string }) => {
    const cleanQuery = omitTabFromQuery(query.query)
    const updates = { ...cleanQuery }

    // Truthiness, not `!== undefined`: zion's changeType always sends `{ day: state.day, type }`, and
    // `state.day` is '' until the calendar resolves and it can take the first day off the schedule. A
    // presence check let a type-only change write a meaningless `day=` and, worse, take the replace
    // branch below — which exists for the first day change on a param-less URL, not for an explicit
    // filter click the user should be able to back out of.
    if (filters.day) {
      updates.day = filters.day
    }

    if (filters.type !== undefined) {
      updates.type = filters.type
    }

    if (Object.keys(query.query).length === 0 && filters.day) {
      // No existing params yet: replace (not push) so back-navigation doesn't land on a
      // param-less copy of this page. setQuery can't do this (push vs replace is fixed at
      // the useQueryParams level), so build the URL and replace directly.
      history.replace(`${location.pathname}?${stringifyQuery(updates)}`)
    } else {
      query.setQuery(updates)
    }
  }

  const onFilterChange = useCallback((filters: { day?: string; type?: string }) => {
    onFilterChangeRef.current?.(filters)
  }, [])

  return (
    <ZionScheduleProvider
      user={userObj}
      calendar={{ data: calendarData, loading: calendarLoading }}
      mySchedule={mySchedule}
      isMySchedulePage={isMySchedulePage}
      initialFilters={initialFilters}
      onFilterChange={onFilterChange}
    >
      {children}
    </ZionScheduleProvider>
  )
}
